import React from 'react';

import { tableStore } from '../../../stores/table-store';
import { buildScriptFileNames } from '../../../stores/utils/build-script-file-names';
import { placeholderScript } from '../../../utils/placeholder-script';

/**
 * The script to upload for a command the static catalog doesn't have — an
 * agent template's command (`agent-template-items.ts`). A shared helper
 * (HasValue, YesNo) is the real script; anything else is a placeholder that
 * answers "false" so the agent takes its not-found path until someone
 * replaces it in Actions. `null` when the solution already has a script by
 * that name: a placeholder never overwrites a real one.
 */
export const fallbackScriptFor = (
  name: string,
  rows: { command?: string | null; decisionVariable?: string | null }[],
  existing: ReadonlySet<string>
): string | null => {
  const command = name.replace(/\.py$/, '');
  if (existing.has(command)) return null;
  const decisionVariable =
    rows.find((row) => row.command?.trim() === command)?.decisionVariable ||
    'success';
  return placeholderScript(command, decisionVariable);
};

/**
 * Fetches the named scripts from the static `/scripts/` catalog and uploads
 * them via the same `handleUpload` a real file-input change event would
 * drive. A name the catalog lacks gets `fallbackScriptFor` instead, read
 * from `rows` (the item's rows).
 *
 * Shared between the click-to-add path (`ResourceLibPanel`) and the drag-
 * and-drop path (`table.tsx`'s native drop handler) so a Resource Library
 * item's scripts land on the solution the same way regardless of which
 * gesture added it — Project Bobcat (Resource Library drag-and-drop).
 */
export const uploadResourceLibScripts = async (
  scriptNames: string[],
  handleUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>,
  rows: { command?: string | null; decisionVariable?: string | null }[] = []
): Promise<void> => {
  if (scriptNames.length === 0) return;
  // Loaded here, not at the top: the app machine needs a browser, and this
  // module's pure helpers are unit-tested without one.
  const { proStudioService } = await import('../../../xstate/app.xstate');
  const existing = new Set(
    buildScriptFileNames(
      proStudioService.getSnapshot().context.draft?.files ?? [],
      []
    )
  );
  const filePromises = scriptNames.map(async (name) => {
    const response = await fetch(`/scripts/${name}`);

    const contentType = response.headers.get('content-type');

    if (!response.ok || (contentType && contentType.includes('text/html'))) {
      const fallback = rows.length
        ? fallbackScriptFor(name, rows, existing)
        : null;
      if (fallback === null) return null;
      return new File([fallback], name, { type: 'text/x-python' });
    }

    const blob = await response.blob();
    return new File([blob], name, { type: 'text/x-python' });
  });

  const files = await Promise.all(filePromises);

  const dataTransfer = new DataTransfer();
  files.forEach((file) => file && dataTransfer.items.add(file));
  // Nothing to upload (e.g. the solution's own scripts, already there).
  if (!dataTransfer.files.length) return;

  const fakeEvent = {
    target: { files: dataTransfer.files },
  } as unknown as React.ChangeEvent<HTMLInputElement>;

  await handleUpload(fakeEvent);
};

/**
 * `item.data`'s `.command` values, `.py`-suffixed — the scripts a Resource
 * Library row set needs uploaded. A native command (system or library, see
 * `stores/utils/action-node-names.ts`) is built into the platform and has no
 * script, so it is left out.
 */
export const scriptNamesFor = (
  data: { command?: string | null }[],
  nativeCommands: readonly string[] = tableStore.actionNodeNames
): string[] => {
  const native = new Set(nativeCommands);
  return data
    .map((row) => row.command?.trim())
    .filter((cmd): cmd is string => Boolean(cmd) && !native.has(cmd!))
    .map((cmd) => `${cmd}.py`);
};
